import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listStayEnhancements, type StayEnhancement } from "@/lib/enhancements.functions";
import { responsiveImg } from "@/lib/responsive-image";
import { useGalleryGestureLock } from "@/hooks/use-gallery-gesture-lock";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

type Props = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  className?: string;
};

export default function StayEnhancementsSection({
  eyebrow = "Stay Enhancements",
  title = "Make it your own",
  subtitle = "Tap any card for details and photos.\n",
  className = "border-t border-foreground/10 bg-background px-4 sm:px-6 py-16 sm:py-20",
}: Props) {
  const fetchEnhancements = useServerFn(listStayEnhancements);
  const { data: enhancements = [] } = useQuery({
    queryKey: ["enhancements"],
    queryFn: () => fetchEnhancements(),
  });
  const [open, setOpen] = useState<StayEnhancement | null>(null);
  useGalleryGestureLock(open !== null);

  if (enhancements.length === 0) return null;

  return (
    <section className={className}>
      <div className="max-w-2xl lg:max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
            {eyebrow}
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl text-foreground">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-2 text-sm text-foreground/60 font-light whitespace-pre-line">{subtitle}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {enhancements.map((en) => (
            <button
              key={en.id}
              type="button"
              onClick={() => setOpen(en)}
              className="group relative block w-full overflow-hidden rounded-xl border border-foreground/10 bg-muted aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-gold/60 active:scale-[0.99] transition-transform"
            >
              {en.cover_image_url ? (() => {
                const cover = responsiveImg(
                  en.cover_image_url,
                  "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
                );
                return (
                  <img
                    src={cover.src}
                    srcSet={cover.srcSet}
                    sizes={cover.sizes}
                    alt={en.label}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                    draggable={false}
                  />
                );
              })() : (
                <div className="absolute inset-0 bg-gradient-to-br from-foreground/10 to-foreground/5" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-left">
                <h4 className="font-serif text-2xl sm:text-3xl text-foreground text-shadow-soft">
                  {en.label}
                </h4>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent hideCloseButton className="gallery-zoom-lock max-w-lg lg:max-w-[1200px] bg-background border-foreground/15 max-h-[85svh] overflow-y-auto">
          {open && (
            <>
              <button
                type="button"
                onClick={() => setOpen(null)}
                aria-label="Close"
                className="sticky top-0 z-20 justify-self-end -mt-2 -mr-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/70 border border-white/25 text-foreground hover:bg-black/90 transition shadow-lg focus:outline-none focus:ring-2 focus:ring-gold/60"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
              <DialogHeader>
                <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-1">
                  Stay Enhancement
                </div>
                <DialogTitle className="font-serif text-3xl text-foreground">
                  {open.label}
                </DialogTitle>
                {open.description && (
                  <DialogDescription className="text-foreground/70 font-light whitespace-pre-line text-left">
                    {open.description}
                  </DialogDescription>
                )}
              </DialogHeader>
              {open.gallery.length > 0 && (
                <div className="gallery-zoom-lock mt-4 space-y-4">
                  {open.gallery.map((img, i) => {
                    const r = responsiveImg(
                      img.url,
                      "(min-width: 1024px) 1100px, 100vw",
                    );
                    return (
                      <img
                        key={img.id}
                        src={r.src}
                        srcSet={r.srcSet}
                        sizes={r.sizes}
                        alt={`${open.label} ${i + 1}`}
                        className="w-full rounded-md border border-foreground/10"
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
